import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Container,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SelectChangeEvent } from '@mui/material/Select';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-hot-toast';
import EditIcon from '@mui/icons-material/Edit';
import { getConfig } from '../config';
interface Company {
  id: number;
  name: string;
}

interface Site {
  id: number;
  company_id: number;
  name: string;
}

interface SaleItem {
  id?: number;
  item: string;
  amount: string | number;
  state?: string;
  release_date?: string | null;
  created_at?: string;
  purpose: string;
  radius?: string;  // 🔥 R값
  length?: string;  // 🔥 길이
  specification?: string;
  completion_date?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

interface Sale {
  id: number;
  contract_date: string;
  company: {
    id: number;
    name: string;
  };
  site: {
    id: number;
    name: string;
  };
  total_amount: number;
  estimate_file?: string;
  created_at: string;
  items: SaleItem[];
}
//const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const { API_BASE_URL } = getConfig();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true,
  timeout: 10000 // 10초 타임아웃 설정
});

// 재시도 설정
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;

// 에러 메시지 포맷팅 함수 개선
const formatErrorMessage = (error: any): string => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // 서버 응답이 있는 경우
      const detail = error.response.data.detail;
      if (typeof detail === 'string') {
        return detail;
      }
      return '서버에서 오류가 발생했습니다.';
    } else if (error.request) {
      // 요청은 보냈지만 응답을 받지 못한 경우
      if (error.code === 'ECONNABORTED') {
        return '서버 응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.';
      }
      return '서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.';
    }
    return '요청 처리 중 오류가 발생했습니다.';
  }
  return '알 수 없는 오류가 발생했습니다.';
};

// 재시도 로직 추가
const retryRequest = async (fn: () => Promise<any>, retries = 3, delay = 1000): Promise<any> => {
  try {
    return await fn();
  } catch (error) {
    if (retries === 0) {
      throw error;
    }
    console.log(`재시도 남은 횟수: ${retries}. ${delay}ms 후 재시도합니다.`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryRequest(fn, retries - 1, delay * 2);
  }
};

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    
    if (error.response) {
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('isLoggedIn');
        window.location.href = '/login';
        return Promise.reject(new Error('인증이 만료되었습니다. 다시 로그인해주세요.'));
      }
      
      if (error.response.status === 500) {
        console.error('Server Error:', error.response.data);
        return Promise.reject(new Error('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'));
      }
    }
    
    return Promise.reject(error);
  }
);

// 파일 입력 스타일링
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const SalesRegister: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [sites, setSites] = useState<{ [key: number]: Site[] }>({});
  const [sales, setSales] = useState<Sale[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [formData, setFormData] = useState<{
    company_id: string;
    site_id: string;
    contract_date: string;
    estimate_file: File | null;
    items: SaleItem[];  
  }>({
    company_id: '',
    site_id: '',
    contract_date: '',
    estimate_file: null,
    items: [{ item: '', amount: '', purpose: '' }],
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);

  // 자재 용도 옵션
  const purposeOptions = [
    { value: '라이닝폼', label: '라이닝폼' },
    { value: '철근대차', label: '철근대차' },
    { value: '새들', label: '새들' },
    { value: '차량용 횡갱', label: '차량용 횡갱' },
    { value: '대인용 횡갱', label: '대인용 횡갱' },
    { value: '차량갱 접속부', label: '차량갱 접속부' },
    { value: '대인갱 접속부', label: '대인갱 접속부' },
    { value: '벨마우스', label: '벨마우스' },
    { value: '중앙벽체', label: '중앙벽체' }
  ];

  useEffect(() => {
    fetchCompanies();
    fetchSales();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await retryRequest(
        () => api.get('/api/companies'),
        3,
        1000
      );
      setCompanies(response.data);
    } catch (err) {
      const errorMessage = formatErrorMessage(err);
      console.error('업체 목록 조회 오류:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async (companyId: string) => {
    if (!companyId) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await retryRequest(
        () => api.get(`/api/companies/${companyId}/sites`),
        3,
        1000
      );
      setSites(prev => ({ ...prev, [parseInt(companyId)]: response.data }));
    } catch (err) {
      const errorMessage = formatErrorMessage(err);
      console.error('현장 목록 조회 오류:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchSales = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await retryRequest(
        () => api.get('/api/sales'),
        MAX_RETRIES,
        INITIAL_RETRY_DELAY
      );
      console.log('매출 목록 응답:', response.data); // 디버깅을 위한 로그 추가
      setSales(response.data);
    } catch (err) {
      const errorMessage = formatErrorMessage(err);
      console.error('매출 목록 조회 오류:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (event: SelectChangeEvent<string>) => {
    const companyId = event.target.value;
    setFormData(prev => ({
      ...prev,
      company_id: companyId,
      site_id: '',
    }));
    if (companyId) {
      fetchSites(companyId);
    }
  };

  const handleSiteChange = (event: SelectChangeEvent<string>) => {
    setFormData(prev => ({
      ...prev,
      site_id: event.target.value,
    }));
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleItemChange = (index: number, field: keyof SaleItem, value: string | number) => {
    const newItems = [...formData.items];
    if (field === 'amount') {
      // amount가 0이거나 빈 문자열일 경우 빈 문자열로 설정
      newItems[index] = {
        ...newItems[index],
        [field]: value === 0 || value === '' ? '' : value
      };
    } else {
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };
    }
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { item: '', amount: '', purpose: '' }]  // amount를 빈 문자열로 초기화
    });
  };

  const removeItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleEditClick = async (sale: Sale) => {
    await fetchSites(String(sale.company.id));

    setFormData({
      company_id: String(sale.company.id),
      site_id: String(sale.site.id),
      contract_date: sale.contract_date,
      estimate_file: null,
      items: sale.items.map(item => {
        let radius: string | undefined = '';
        let length: string | undefined = '';

        if (item.specification) {
          const match = item.specification.match(/^(\d+)[Rr]\s*x\s*(\d+)[Mm]$/);
          if (match) {
            radius = match[1];
            length = match[2];
          }
        }

        return {
          id: item.id,
          item: item.item,
          amount: item.amount,
          purpose: item.purpose,
          radius,
          length,
          specification: item.specification || '',
          // 🔥 기존 정보도 같이 보존
          state: item.state || '대기',
          release_date: item.release_date || null,
          completion_date: item.completion_date || null,
          start_date: item.start_date || null,
          end_date: item.end_date || null,
        };
      })
    });

    setSelectedDate(new Date(sale.contract_date));
    setSelectedSale(sale);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    if (!selectedDate) {
      setError('계약일을 선택해주세요.');
      setLoading(false);
      return;
    }

    try {
      const contractDate = format(selectedDate, 'yyyy-MM-dd');
      const companyId = parseInt(formData.company_id);
      const siteId = parseInt(formData.site_id);
      const items = formData.items.map((item) => ({
        id: item.id, // 수정 모드라면 id 필요
        item: item.item.trim(),
        amount: parseInt(item.amount as string),
        purpose: item.purpose,
        specification: item.specification || '',
        // 상태 유지용 필드
        state: item.state,
        release_date: item.release_date,
        completion_date: item.completion_date,
        start_date: item.start_date,
        end_date: item.end_date
      }));

      const formDataObj = new FormData();
      formDataObj.append('contract_date', contractDate);
      formDataObj.append('company_id', String(companyId));
      formDataObj.append('site_id', String(siteId));
      formDataObj.append('items', JSON.stringify(items));
      if (selectedFile) {
        formDataObj.append('estimate_file', selectedFile);
      }

      const isEditMode = selectedSale !== null;

      const response = isEditMode
        ? await api.put(`/api/sales/${selectedSale!.id}`, formDataObj, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        : await api.post('/api/sales', formDataObj, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

      await fetchSales();

      setFormData({
        company_id: '',
        site_id: '',
        contract_date: '',
        estimate_file: null,
        items: [{ item: '', amount: '', purpose: '' }]
      });
      setSelectedDate(new Date());
      setSelectedFile(null);
      setSelectedSale(null);
      setError('');

      enqueueSnackbar(
        isEditMode ? '매출이 성공적으로 수정되었습니다.' : '매출이 성공적으로 등록되었습니다.',
        { variant: 'success', autoHideDuration: 3000 }
      );
    } catch (err) {
      const errorMessage = formatErrorMessage(err);
      console.error('매출 등록/수정 오류:', err);
      setError(errorMessage);
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownloadEstimate = async (saleId: number) => {
    try {
      const { data } = await api.get(`/api/sales/${saleId}/files/estimate`);
      const presignedUrl = data.url;

      const link = document.createElement('a');
      link.href = presignedUrl;
      link.setAttribute('download', ''); // 이 경우 S3가 보내는 파일 이름이 그대로 적용됨
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('파일 다운로드 실패:', error);
      enqueueSnackbar('견적서 다운로드 중 오류가 발생했습니다.', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  // MIME 타입에 따른 파일 확장자 반환
  const getFileExtension = (mimeType: string): string => {
    const mimeToExt: { [key: string]: string } = {
      'application/pdf': '.pdf',
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      'application/vnd.ms-powerpoint': '.ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
      'text/plain': '.txt',
      'text/csv': '.csv'
    };
    return mimeToExt[mimeType] || '';
  };

  // 총 계약금액 계산 함수 추가
  const calculateTotalAmount = () => {
    return formData.items.reduce((sum, item) => sum + (parseInt(item.amount as string) || 0), 0);
  };

  // 매출 삭제 함수
  const handleDeleteSale = async (saleId: number) => {
    try {
      if (!window.confirm('매출을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
        return;
      }

      await api.delete(`/api/sales/${saleId}`);
      
      enqueueSnackbar('매출이 성공적으로 삭제되었습니다.', {
        variant: 'success',
        autoHideDuration: 3000,
      });
      
      // 매출 목록 새로고침
      fetchSales();
    } catch (error) {
      console.error('API Error:', error);
      if (axios.isAxiosError(error)) {
        enqueueSnackbar(error.response?.data?.detail || '매출 삭제 중 오류가 발생했습니다.', {
          variant: 'error',
          autoHideDuration: 5000,
        });
      } else {
        enqueueSnackbar('알 수 없는 오류가 발생했습니다.', {
          variant: 'error',
          autoHideDuration: 5000,
        });
      }
    }
  };

  // 상태에 따른 색상 반환 함수
  const getStateColor = (state: string) => {
    switch (state) {
      case '대기':
        return 'default';
      case '생산':
        return 'warning';
      case '생산완료':
        return 'info';
      case '출고':
        return 'success';
      case '계산서발행':
        return 'primary';
      default:
        return 'default';
    }
  };

  const handleSpecChange = (index: number, field: 'radius' | 'length', value: string) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;

    const radius = newItems[index].radius?.trim();
    const length = newItems[index].length?.trim();

    if (radius && length) {
      newItems[index].specification = `${radius}R x ${length}M`;
    } else {
      newItems[index].specification = '';
    }

    setFormData({ ...formData, items: newItems });
  };
  // 삭제 확인 다이얼로그 열기
  const handleDeleteClick = (sale: Sale) => {
    setSelectedSale(sale);
    setDeleteDialogOpen(true);
  };

  // 매출 삭제 처리
  const handleDelete = async () => {
    if (!selectedSale) return;

    try {
      await api.delete(`/api/sales/${selectedSale.id}`);

      // 삭제 성공 후 목록 새로고침
      fetchSales();
      setDeleteDialogOpen(false);
      setSelectedSale(null);
      enqueueSnackbar('매출이 삭제되었습니다.', { variant: 'success' });
    } catch (error) {
      console.error('매출 삭제 오류:', error);
      enqueueSnackbar('매출 삭제 중 오류가 발생했습니다.', { variant: 'error' });
    }
  };
  const extractFileNameFromUrl = (url: string) => {
    try {
      return decodeURIComponent(url.split('/').pop() || '');
    } catch {
      return '';
    }
  };
  // 삭제 확인 다이얼로그 컴포넌트
  const DeleteConfirmDialog = () => (
    <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
      <DialogTitle>매출 삭제 확인</DialogTitle>
      <DialogContent>
        <DialogContentText>
          정말로 이 매출을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
        <Button onClick={handleDelete} color="error" autoFocus>
          삭제
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          매출 등록
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>업체 선택</InputLabel>
                <Select
                  value={formData.company_id}
                  onChange={handleCompanyChange}
                  label="업체 선택"
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>현장 선택</InputLabel>
                <Select
                  value={formData.site_id}
                  onChange={handleSiteChange}
                  label="현장 선택"
                  disabled={!formData.company_id}
                >
                  {sites[parseInt(formData.company_id)]?.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="계약일"
                  value={selectedDate}
                  onChange={handleDateChange}
                  format="yyyy-MM-dd"
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                계약 항목
              </Typography>
              
              {formData.items.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="항목"
                      name="item"
                      value={item.item}
                      onChange={(e) => handleItemChange(index, 'item', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="계약금액 (천원)"
                      name="amount"
                      type="number"
                      value={item.amount}
                      onChange={(e) => handleItemChange(index, 'amount', Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">천원</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={1.7}>
                    <TextField
                      fullWidth
                      label="R값"
                      value={item.radius || ''}
                      onChange={(e) => handleSpecChange(index, 'radius', e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={1.7}>
                    <TextField
                      fullWidth
                      label="길이"
                      value={item.length || ''}
                      onChange={(e) => handleSpecChange(index, 'length', e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">M</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControl sx={{ flex: 1 }}>
                      <InputLabel>카테고리</InputLabel>
                      <Select
                        value={item.purpose}
                        onChange={(e) => handleItemChange(index, 'purpose', e.target.value)}
                        label="자재 용도"
                      >
                        {purposeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={0.5} sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      onClick={() => removeItem(index)}
                      disabled={formData.items.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              
              <Button
                variant="outlined"
                onClick={addItem}
                startIcon={<AddIcon />}
                sx={{ mt: 1 }}
              >
                항목 추가
              </Button>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="h6" sx={{ mr: 2 }}>
                  총 계약금액: {calculateTotalAmount().toLocaleString()}천원
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                sx={{ mt: 2 }}
              >
                견적서 업로드
                <VisuallyHiddenInput
                  type="file"
                  onChange={handleFileChange}
                  accept="*"
                />
              </Button>

              {/* 현재 선택된 파일 표시 */}
              {selectedFile && (  
                <Typography variant="body2" sx={{ mt: 1 }}>
                  선택된 파일: {selectedFile.name}
                </Typography>
              )}

              {/* 수정 중이고 선택된 파일 없을 때 기존 파일 표시 */}
              {selectedSale?.estimate_file && !selectedFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  기존 파일: {extractFileNameFromUrl(selectedSale.estimate_file)}
                </Typography>
              )}
            </Grid>


            <Grid item xs={12} sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                  sx={{ minWidth: 120 }}
                >
                  {loading ? <CircularProgress size={24} /> : '등록'}
                </Button>
                {error && (
                  <Alert severity="error" sx={{ flex: 1, ml: 2 }}>
                    {error}
                  </Alert>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Paper sx={{ mt: 4, p: 3 }}>
        <Typography variant="h5" gutterBottom>
          매출 목록
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="15%">계약일</TableCell>
                  <TableCell width="20%">업체명</TableCell>
                  <TableCell width="20%">현장</TableCell>
                  <TableCell width="15%" align="right">총 계약금액(천원)</TableCell>
                  <TableCell width="10%" align="center">견적서</TableCell>
                  <TableCell width="10%" align="center">수정</TableCell>
                  <TableCell width="10%" align="center">삭제</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sales.map((sale) => (
                  <React.Fragment key={sale.id}>
                    <TableRow>
                      <TableCell width="15%">{sale.contract_date}</TableCell>
                      <TableCell width="20%">{sale.company.name}</TableCell>
                      <TableCell width="20%">{sale.site.name}</TableCell>
                      <TableCell width="15%" align="right">
                        {sale.total_amount.toLocaleString()} 
                      </TableCell>
                      <TableCell width="10%" align="center">
                        {sale.estimate_file && (
                          <IconButton
                            onClick={() => handleDownloadEstimate(sale.id)}
                            size="small"
                            title="견적서 다운로드"
                            color="primary"
                          >
                            <DownloadIcon />
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleEditClick(sale)}
                          title="매출 수정"
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell width="10%" align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(sale)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {/* 하부 항목 표시 */}
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell width="20%">항목</TableCell>
                              <TableCell width="20%">규격</TableCell>
                              <TableCell width="20%" align="right">금액(천원)</TableCell>
                              <TableCell width="25%" align="center">상태</TableCell>
                              <TableCell width="25%" align="center">출고일</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sale.items.map((item) => (
                              <TableRow key={item.id} sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell width="20%">{item.item}</TableCell>
                                <TableCell width="20%">{item.specification}</TableCell>
                                <TableCell width="20%" align="right">
                                  {item.amount.toLocaleString()}
                                </TableCell>
                                <TableCell width="25%" align="center">
                                  <Chip
                                    label={item.state || '대기'}
                                    color={getStateColor(item.state || '대기')}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell width="25%" align="center">
                                  {item.release_date || '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <DeleteConfirmDialog />
    </Container>
  );
};

export default SalesRegister; 